// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { customerPartNumberStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsCustomerPartNumber";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductBrand from "@pacific/Overrides/Components/ProductBrand";
import { pageTitleStyles } from "@pacific/Overrides/Widgets/ProductDetails/ProductDetailsPageTitle";
import { fontConfig } from "@pacific/XDL/PreStyle/typography";
import { ellipsis } from "@pacific/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = {
    product: ProductModel;
};

export interface ProductDetailsHeaderContentStyles {
    container?: InjectableCss;
    imgSection?: InjectableCss;
    detailsSection?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    productParts?: InjectableCss;
    partContainer?: InjectableCss;
    partName?: TypographyPresentationProps;
    partValue?: TypographyPresentationProps;
    brandStyles?: ProductBrandStyles;
}

export const productDetailsHeaderContentStyles: ProductDetailsHeaderContentStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },

    imgSection: {
        css: css`
            flex: 0 0 66px;
            margin-right: 20px;
            > div[data-test-selector="primary-image-container"] {
                width: 66px;
                height: 66px;
                margin-bottom: 0;
                text-align: center;
                > div {
                    min-height: auto !important; // important in case of sriv
                }
                > button[data-test-selector="productDetails-image-zoom"] {
                    display: none;
                }

                img {
                    object-fit: contain;
                    height: 66px;
                    width: 66px;
                }
            }
        `,
    },
    detailsSection: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    productTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 26px;
            ${ellipsis(1)};
        `,
    },
    productParts: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;

            [data-test-selector="pdp-customerPartNumber"] > p {
                font-weight: 500;
                span {
                    font-weight: 600;
                }
            }
        `,
    },
    partContainer: {
        css: css`
            min-width: fit-content;
            margin-right: 20px;
            line-height: 1;
            &:last-child {
                margin: 0;
            }
        `,
    },
    partName: { ...customerPartNumberStyles.labelText },
    partValue: { ...customerPartNumberStyles.valueText },
    brandStyles: {
        nameText: {
            css: css`
                width: auto;
                padding-right: 20px;
                margin-bottom: 0;
            `,
        },
        nameLink: {
            typographyProps: {
                size: 12,
                weight: fontConfig.weight.semiBold,
                fontFamily: fontConfig.fontFamily,
                lineHeight: "18px",
                transform: "uppercase",
            },
            css: css`
                margin: 0 24px 0 0;
                width: auto;
            `,
        },
    },
};

const styles = productDetailsHeaderContentStyles;

const ProductDetailsSummary: FC<Props> = ({ product }) => {
    if (!product) {
        return null;
    }
    const { brand, productTitle, customerProductNumber, manufacturerItem, productNumber, properties, isVariantParent } =
        product;
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.imgSection}>
                {createWidgetElement("ProductDetails/ProductDetailsPrimaryImage", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.detailsSection}>
                {productTitle && <Typography {...styles.productTitle}>{productTitle}</Typography>}
                <StyledWrapper {...styles.productParts}>
                    {product.brand && (
                        <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                    )}
                    <StyledWrapper {...styles.partContainer}>
                        <Typography {...styles.partName}>{translate("MFR #")}</Typography>
                        <Typography {...styles.partValue}>{manufacturerItem}</Typography>
                    </StyledWrapper>
                    {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                        fields: {},
                    })}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductDetailsSummary;
